import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Stack,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  MoreVert,
  AttachMoney,
  Person,
  Email,
  Phone,
  CheckCircleOutline,
  CancelOutlined,
  CompareArrows,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface QuoteResponse {
  contractorId: string;
  contractorName: string;
  contractorEmail: string;
  contractorPhone?: string;
  amount: number;
  notes?: string;
  respondedAt: Date;
  status: 'pending' | 'accepted' | 'declined';
  isAgencyQuote?: boolean;
}

interface JobQuote {
  jobId: string;
  jobType: string;
  jobNotes?: string;
  agencyQuote?: {
    amount: number;
    notes?: string;
    sentAt: Date;
  };
  contractorResponses: QuoteResponse[];
  acceptedQuote?: {
    contractorId: string;
    contractorName: string;
    amount: number;
    notes?: string;
    decidedAt: Date;
  };
  status: 'draft' | 'sent' | 'responded' | 'accepted' | 'declined';
  createdAt: Date;
}

interface QuoteManagementProps {
  jobQuotes: JobQuote[];
  onAcceptQuote: (jobId: string, contractorId: string) => Promise<void>;
  onDeclineQuote: (jobId: string, contractorId: string) => Promise<void>;
  onViewQuoteDetails: (jobId: string, contractorId: string) => void;
  loading?: boolean;
}

export const QuoteManagement: React.FC<QuoteManagementProps> = ({
  jobQuotes,
  onAcceptQuote,
  onDeclineQuote,
  onViewQuoteDetails,
  loading = false,
}) => {
  const [selectedJob, setSelectedJob] = useState<JobQuote | null>(null);
  const [quoteDetailsOpen, setQuoteDetailsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContractor, setSelectedContractor] = useState<QuoteResponse | null>(null);

  const handleAcceptQuote = async (jobId: string, contractorId: string) => {
    try {
      await onAcceptQuote(jobId, contractorId);
      setQuoteDetailsOpen(false);
      setSelectedJob(null);
    } catch (error) {
      console.error('Error accepting quote:', error);
    }
  };

  const handleDeclineQuote = async (jobId: string, contractorId: string) => {
    try {
      await onDeclineQuote(jobId, contractorId);
      setQuoteDetailsOpen(false);
      setSelectedJob(null);
    } catch (error) {
      console.error('Error declining quote:', error);
    }
  };

  const handleViewQuoteDetails = (job: JobQuote, contractor: QuoteResponse) => {
    setSelectedJob(job);
    setSelectedContractor(contractor);
    setQuoteDetailsOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, contractor: QuoteResponse) => {
    setAnchorEl(event.currentTarget);
    setSelectedContractor(contractor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedContractor(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'declined': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted': return <CheckCircle />;
      case 'declined': return <Cancel />;
      case 'pending': return <Visibility />;
      default: return null;
    }
  };

  const formatCurrency = (amount: any) => {
    const n = Number(amount);
    if (!isFinite(n)) return '-';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(n);
  };

  const getQuoteComparison = (responses: QuoteResponse[]) => {
    if (responses.length <= 1) return null;
    
    const amounts = responses.map(r => r.amount);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    
    return { min, max, avg, count: amounts.length };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography>Loading quotes...</Typography>
      </Box>
    );
  }

  if (jobQuotes.length === 0) {
    return (
      <Alert severity="info">
        No quotes available for this property.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Quote Management
      </Typography>
      
      {jobQuotes.map((jobQuote) => {
        const comparison = getQuoteComparison(jobQuote.contractorResponses);
        const hasAcceptedQuote = jobQuote.acceptedQuote;
        
        return (
          <Card key={jobQuote.jobId} sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {jobQuote.jobType.charAt(0).toUpperCase() + jobQuote.jobType.slice(1)} Job
                  </Typography>
                  {jobQuote.jobNotes && (
                    <Typography variant="body2" color="text.secondary">
                      {jobQuote.jobNotes}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={jobQuote.status}
                  color={getStatusColor(jobQuote.status) as any}
                  size="small"
                />
              </Box>

              {jobQuote.agencyQuote && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Agency Offer
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachMoney color="primary" />
                    <Typography variant="h6" color="primary">
                      {formatCurrency(jobQuote.agencyQuote.amount)}
                    </Typography>
                    {jobQuote.agencyQuote.notes && (
                      <Typography variant="body2" color="text.secondary">
                        — {jobQuote.agencyQuote.notes}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {hasAcceptedQuote && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircle />
                    <Typography variant="body2">
                      <strong>Accepted:</strong> {hasAcceptedQuote.contractorName} - {formatCurrency(hasAcceptedQuote.amount)}
                    </Typography>
                  </Box>
                </Alert>
              )}

              {comparison && (
                <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Quote Comparison ({comparison.count} responses)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">Lowest</Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(comparison.min)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">Average</Typography>
                        <Typography variant="h6">
                          {formatCurrency(comparison.avg)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">Highest</Typography>
                        <Typography variant="h6" color="error.main">
                          {formatCurrency(comparison.max)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Contractor</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Response Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobQuote.contractorResponses.map((response) => (
                      <TableRow key={response.contractorId}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {(response.contractorName?.charAt(0) || '?').toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {response.contractorName || 'Unknown Contractor'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {response.contractorEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney color="primary" />
                            <Typography variant="body1" fontWeight="bold">
                              {formatCurrency(response.amount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(response.status)}
                            label={response.status}
                            color={getStatusColor(response.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {(() => {
                              const ts: any = (response as any)?.respondedAt || (response as any)?.sentAt;
                              const d = ts ? new Date(ts) : null;
                              try {
                                return (d && !isNaN(d.getTime())) ? format(d as Date, 'MMM dd, yyyy HH:mm') : '-';
                              } catch {
                                return '-';
                              }
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={1}>
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewQuoteDetails(jobQuote, response)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            
                            {!hasAcceptedQuote && response.status === 'pending' && (
                              <>
                                <Tooltip title="Approve quote">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleAcceptQuote(jobQuote.jobId, response.contractorId)}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Decline quote">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeclineQuote(jobQuote.jobId, response.contractorId)}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, response)}
                            >
                              <MoreVert />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );
      })}

      {/* Quote Details Dialog */}
      <Dialog
        open={quoteDetailsOpen}
        onClose={() => setQuoteDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Quote Details
          {selectedContractor && (
            <Typography variant="body2" color="text.secondary">
              {selectedContractor.contractorName} • {formatCurrency(selectedContractor.amount)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedContractor && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Contractor Information
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person fontSize="small" />
                      <Typography variant="body2">{selectedContractor.contractorName}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Email fontSize="small" />
                      <Typography variant="body2">{selectedContractor.contractorEmail}</Typography>
                    </Box>
                    {selectedContractor.contractorPhone && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Phone fontSize="small" />
                        <Typography variant="body2">{selectedContractor.contractorPhone}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Quote Information
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AttachMoney fontSize="small" />
                      <Typography variant="h6">{formatCurrency(selectedContractor.amount)}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        icon={getStatusIcon(selectedContractor.status)}
                        label={selectedContractor.status}
                        color={getStatusColor(selectedContractor.status) as any}
                        size="small"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Responded: {format(new Date(selectedContractor.respondedAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
              
              {selectedContractor.notes && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2">{selectedContractor.notes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedContractor && selectedContractor.status === 'pending' && !selectedJob?.acceptedQuote && (
            <>
              <Button
                color="error"
                onClick={() => handleDeclineQuote(selectedJob!.jobId, selectedContractor.contractorId)}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleAcceptQuote(selectedJob!.jobId, selectedContractor.contractorId)}
              >
                Approve Quote
              </Button>
            </>
          )}
          <Button onClick={() => setQuoteDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedContractor && selectedJob) {
            handleViewQuoteDetails(selectedJob, selectedContractor);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        {selectedContractor && selectedContractor.status === 'pending' && !selectedJob?.acceptedQuote && (
          <>
            <MenuItem onClick={() => {
              if (selectedContractor && selectedJob) {
                handleAcceptQuote(selectedJob.jobId, selectedContractor.contractorId);
              }
              handleMenuClose();
            }}>
              <ListItemIcon>
                <CheckCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Approve Quote</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => {
              if (selectedContractor && selectedJob) {
                handleDeclineQuote(selectedJob.jobId, selectedContractor.contractorId);
              }
              handleMenuClose();
            }}>
              <ListItemIcon>
                <Cancel fontSize="small" />
              </ListItemIcon>
              <ListItemText>Decline Quote</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};





